/* eslint-disable react/no-array-index-key */

import CircularProgress from "@material-ui/core/CircularProgress";
import Collapse from "@material-ui/core/Collapse";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import React, { FC, MouseEvent, SetStateAction, useEffect, useRef, useState } from "react";
import { Theme } from "@material-ui/core/styles";
import { styled } from "@material-ui/styles";
import { useDispatch, useSelector } from "react-redux";

import StateProvinceSelectionDasboard from "../../CampaignCreationCustomSlides/StateProvinceSelectionSlide/components/StateProvinceSelectionDasboard";
import api from "../../../api";
import { ManageLink } from "../../../components/redesign/elements/ManageLink/ManageLink";
import { ReduxApplicationState } from "../../../types/ReduxTypes/ReduxApplicationState";
import { StateProvinceSet } from "../../../types/campaignTypes";
import {
  getGeoTargetOptions,
  setSelectedGeoTargetOptions,
  setSelectedStateProvinceSetId,
} from "../../../redux/reducers/campaignToCreate";
import { getStateNamesByIds } from "./stateProvinceSetUtils";

type Props = {
  panelMode?: boolean;
};

type TabKey = "set" | "manual";

const PanelRoot = styled("div")({ display: "flex", flexDirection: "column", gap: 12 });
const Card = styled("div")(({ theme }: { theme: Theme }) => ({
  background: theme.palette.brand.white,
  border: `1px solid ${theme.palette.brand.gray200}`,
  borderRadius: 10,
  overflow: "hidden",
}));
const TableEl = styled("table")({ width: "100%", borderCollapse: "collapse" });
const Thead = styled("thead")(({ theme }: { theme: Theme }) => ({ background: theme.palette.brand.gray50 }));
const Th = styled("th")(({ theme }: { theme: Theme }) => ({
  padding: "10px 16px",
  textAlign: "left" as const,
  fontSize: 10,
  fontWeight: 600,
  textTransform: "uppercase" as const,
  letterSpacing: "0.08em",
  color: theme.palette.brand.gray500,
  borderBottom: `1px solid ${theme.palette.brand.gray200}`,
}));
const Td = styled("td")(({ theme }: { theme: Theme }) => ({
  padding: "0 16px",
  fontSize: 13,
  color: theme.palette.brand.gray900,
  verticalAlign: "middle" as const,
}));
const ClickableRow = styled("tr")(({ theme }: { theme: Theme }) => ({
  height: 52,
  borderBottom: `1px solid ${theme.palette.brand.gray200}`,
  cursor: "pointer",
  "&:hover": { background: theme.palette.brand.gray50 },
}));
const ExpandRow = styled("tr")(({ theme }: { theme: Theme }) => ({
  "& td": { padding: "0 !important", borderBottom: `1px solid ${theme.palette.brand.gray200}` },
}));
const CountCell = styled("span")({ display: "inline-flex", alignItems: "center", gap: 4 });
const ExpandIconButton = styled(IconButton)(({ theme }: { theme: Theme }) => ({
  padding: 2,
  color: theme.palette.brand.gray500,
}));
const Radio = styled("div")(({ theme }: { theme: Theme }) => ({
  width: 18,
  height: 18,
  borderRadius: "50%",
  border: `2px solid ${theme.palette.brand.gray300}`,
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "&[data-selected='true']": { borderColor: theme.palette.brand.coral },
  "& span": { width: 10, height: 10, borderRadius: "50%", background: theme.palette.brand.coral },
}));
const EmptyState = styled("div")(({ theme }: { theme: Theme }) => ({
  padding: "32px 20px",
  textAlign: "center" as const,
  fontSize: 14,
  color: theme.palette.brand.gray500,
  "& a": { color: theme.palette.brand.coral, textDecoration: "none", fontWeight: 500 },
  "& a:hover": { textDecoration: "underline" },
}));
const LoadingState = styled("div")({ padding: "32px", display: "flex", justifyContent: "center" });
const AdvertiserPrompt = styled(Typography)(({ theme }: { theme: Theme }) => ({
  color: theme.palette.brand.gray500,
  fontSize: 14,
  padding: "16px 0",
}));
const TagsWrapper = styled("div")({ display: "flex", flexWrap: "wrap", gap: 6, padding: "10px 16px 12px" });
const TagGroupLabel = styled("span")(({ theme }: { theme: Theme }) => ({
  display: "inline-block",
  fontWeight: 600,
  fontSize: 11,
  textTransform: "uppercase" as const,
  letterSpacing: "0.06em",
  color: theme.palette.brand.gray500,
  padding: "4px 0 2px",
  width: "100%",
}));
const StateTag = styled("span")(({ theme }: { theme: Theme }) => ({
  display: "inline-block",
  padding: "3px 10px",
  borderRadius: 20,
  fontSize: 12,
  color: theme.palette.brand.gray700,
  background: theme.palette.brand.gray100,
  border: `1px solid ${theme.palette.brand.gray200}`,
}));

const TabBar = styled("div")(({ theme }: { theme: Theme }) => ({
  display: "inline-flex",
  gap: 4,
  padding: 4,
  borderRadius: 10,
  background: theme.palette.brand.gray100,
  alignSelf: "flex-start",
}));
const TabButton = styled("button")(({ theme }: { theme: Theme }) => ({
  border: "none",
  cursor: "pointer",
  background: "transparent",
  color: theme.palette.brand.gray500,
  fontSize: 13,
  fontWeight: 600,
  padding: "7px 16px",
  borderRadius: 8,
  transition: "all 0.15s ease",
  "&[data-active='true']": {
    background: theme.palette.brand.white,
    color: theme.palette.brand.gray900,
    boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
  },
}));
const DashboardWrapper = styled("div")({
  "& > div": { overflowX: "visible", marginTop: 0 },
});

const StateProvinceSetSelectorPanel: FC<Props> = ({ panelMode = false }) => {
  const dispatch = useDispatch();
  const selectedAdvertiser = useSelector((state: ReduxApplicationState) => state.campaignToCreate.selectedAdvertiser);
  const selectedStateProvinceSetId = useSelector(
    (state: ReduxApplicationState) => state.campaignToCreate.selectedStateProvinceSetId,
  );
  const geoTargetOptions = useSelector((s: ReduxApplicationState) => s.campaignToCreate.geoTargetOptions);
  const selectedGeoTargetOptions = useSelector(
    (s: ReduxApplicationState) => s.campaignToCreate.selectedGeoTargetOptions,
  );
  const brandgenAdvertiserId = selectedAdvertiser?.brandgenAdvertiserId ?? null;
  const [sets, setSets] = useState<StateProvinceSet[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<TabKey>("set");

  const hasFetched = useRef(false);

  useEffect(() => {
    if (!hasFetched.current && !geoTargetOptions) {
      hasFetched.current = true;
      dispatch(getGeoTargetOptions());
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!brandgenAdvertiserId) {
      setSets([]);
      return;
    }
    setIsLoading(true);
    const fetchSets = async () => {
      try {
        const { payload } = await api.getStateProvinceSets(brandgenAdvertiserId);
        setSets(Array.isArray(payload) ? payload : []);
      } catch {
        setSets([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchSets();
  }, [brandgenAdvertiserId]);

  const handleSelect = (set: StateProvinceSet) => {
    if (selectedStateProvinceSetId === set.id) {
      dispatch(setSelectedStateProvinceSetId(null));
      dispatch(setSelectedGeoTargetOptions([]));
      return;
    }
    dispatch(setSelectedStateProvinceSetId(set.id));
    dispatch(setSelectedGeoTargetOptions(set.geoTargetIds.map((id) => String(id))));
  };

  const handleManualSetSelected = (value: SetStateAction<string[]>) => {
    const next = typeof value === "function" ? value(selectedGeoTargetOptions) : value;
    dispatch(setSelectedStateProvinceSetId(null));
    dispatch(setSelectedGeoTargetOptions(next));
  };

  const toggleExpand = (id: string, e: MouseEvent) => {
    e.stopPropagation();
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (!selectedAdvertiser)
    return <AdvertiserPrompt>Please select an advertiser to load state & province sets.</AdvertiserPrompt>;

  if (isLoading || !geoTargetOptions) {
    return (
      <LoadingState>
        <CircularProgress size={28} />
      </LoadingState>
    );
  }

  return (
    <PanelRoot>
      {panelMode && (
        <ManageLink>
          To create or manage state & province sets, go to{" "}
          <a href="/state-province" target="_blank" rel="noopener noreferrer">
            States & Provinces
          </a>
          .
        </ManageLink>
      )}
      <TabBar>
        <TabButton type="button" data-active={String(activeTab === "set")} onClick={() => setActiveTab("set")}>
          Pick a set
        </TabButton>
        <TabButton type="button" data-active={String(activeTab === "manual")} onClick={() => setActiveTab("manual")}>
          Choose manually
        </TabButton>
      </TabBar>
      {activeTab === "manual" ? (
        <DashboardWrapper>
          <StateProvinceSelectionDasboard
            geoTargetOptions={geoTargetOptions}
            selectedGeoTargetOptions={selectedGeoTargetOptions}
            setSelectedGeoTargetOptions={handleManualSetSelected}
          />
        </DashboardWrapper>
      ) : sets.length === 0 ? (
        <Card>
          <EmptyState>
            No state & province sets found for <strong>{selectedAdvertiser.name}</strong>.{" "}
          </EmptyState>
        </Card>
      ) : (
        <Card>
          <TableEl>
            <Thead>
              <tr>
                <Th style={{ width: 60 }} />
                <Th>Name</Th>
                <Th>States & Provinces</Th>
              </tr>
            </Thead>
            <tbody>
              {sets.map((set) => {
                const isExpanded = expandedIds.has(set.id);
                const isSelected = selectedStateProvinceSetId === set.id;
                const { us, ca } = getStateNamesByIds(geoTargetOptions, set.geoTargetIds);
                const totalCount = us.length + ca.length;
                return (
                  <React.Fragment key={set.id}>
                    <ClickableRow
                      onClick={() =>
                        setExpandedIds((prev) => {
                          const next = new Set(prev);
                          if (next.has(set.id)) next.delete(set.id);
                          else next.add(set.id);
                          return next;
                        })
                      }
                    >
                      <Td
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelect(set);
                        }}
                      >
                        <Radio data-selected={String(isSelected)}>{isSelected && <span />}</Radio>
                      </Td>
                      <Td>{set.name}</Td>
                      <Td>
                        <CountCell>
                          <ExpandIconButton
                            size="small"
                            aria-label={isExpanded ? "collapse" : "expand"}
                            onClick={(e: MouseEvent) => toggleExpand(set.id, e)}
                          >
                            {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                          </ExpandIconButton>
                          {totalCount.toLocaleString()}
                        </CountCell>
                      </Td>
                    </ClickableRow>
                    <ExpandRow>
                      <td colSpan={3}>
                        <Collapse in={isExpanded} unmountOnExit>
                          <TagsWrapper>
                            {us.length > 0 && (
                              <>
                                <TagGroupLabel>United States</TagGroupLabel>
                                {us.map((name, index) => (
                                  <StateTag key={`us-${name}-${index}`}>{name}</StateTag>
                                ))}
                              </>
                            )}
                            {ca.length > 0 && (
                              <>
                                <TagGroupLabel>Canada</TagGroupLabel>
                                {ca.map((name, index) => (
                                  <StateTag key={`ca-${name}-${index}`}>{name}</StateTag>
                                ))}
                              </>
                            )}
                          </TagsWrapper>
                        </Collapse>
                      </td>
                    </ExpandRow>
                  </React.Fragment>
                );
              })}
            </tbody>
          </TableEl>
        </Card>
      )}
    </PanelRoot>
  );
};

export default StateProvinceSetSelectorPanel;
